import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shared/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { useUsers } from "@shared/api/hooks/useUsers";
import { Plus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

// F-A — admin-only staff user list. Navigation-only: each row opens the
// edit page; all mutating actions live there (keeps the list simple).

const UsersList = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { data: users = [], isLoading } = useUsers();

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/staff/exhibitions");
    }
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="users-list-page">
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate("/staff/exhibitions")}
              variant="ghost"
              size="sm"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-heading font-bold">Users</h1>
          </div>
          <Button
            onClick={() => navigate("/staff/users/new")}
            data-testid="create-user-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create user
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="text-center py-12" data-testid="loading-state">
            Loading...
          </div>
        ) : users.length === 0 ? (
          <div
            className="text-center py-12 border border-border rounded-lg"
            data-testid="empty-state"
          >
            <p className="text-muted-foreground">
              No users yet — click Create user to add one.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Username
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Display name
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Role
                  </th>
                  <th className="text-left px-6 py-3 text-sm font-medium">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/staff/users/${u.id}`)}
                    className="cursor-pointer hover:bg-muted/30"
                    data-testid={`user-row-${u.id}`}
                  >
                    <td className="px-6 py-3 text-sm">{u.username}</td>
                    <td className="px-6 py-3 text-sm">{u.displayName}</td>
                    <td className="px-6 py-3 text-sm">{u.role}</td>
                    <td className="px-6 py-3 text-sm">
                      {u.enabled ? "✓ active" : "✗ disabled"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersList;
